/**
 * Airspace overlay — OpenAIP zone polygons + OSM national parks.
 * Styled to match dronesafetymap.com color conventions.
 * Click opens a "WHERE YOU CLICKED" panel listing all overlapping zones.
 *
 * Data source: public/data/airspace/ chunks (part-*.json + manifest.json)
 *   (OpenAIP zones + national parks merged by pipeline script, coordinate-rounded
 *    and split into <22 MB uncompressed parts by scripts/chunk-geojson.mjs so
 *    Cloudflare Workers Assets can serve them without gzip hacks.)
 */
import mapboxgl from 'mapbox-gl';
import { fetchJSON } from '../utils/fetch-data';

const SOURCE_ID  = 'airspace';
const LAYER_FILL = 'airspace-zones';
const LAYER_LINE = 'airspace-borders';
const ALL_LAYERS = [LAYER_FILL, LAYER_LINE];

/* ── Zone type metadata ────────────────────────────────────────────────────── */

interface ZoneMeta {
  label:     string;
  fillColor: string;
  lineColor: string;
  dash?:     boolean;
}

/**
 * Color conventions follow VFR sectional chart standards adapted for drone use:
 *   Prohibited  → deep red    (absolute no-fly)
 *   Restricted  → red-orange  (authorization required)
 *   Danger      → orange      (active hazards: MOAs, gunnery, etc.)
 *   CTR         → blue        (control zone — Class B/D; needs authorization)
 *   TMA         → magenta     (terminal area — Class C; strongly advisory)
 *   TRA         → amber       (temporary reservation, dashed)
 *   ADIZ        → purple      (air-defense ID zone, dashed)
 *   RMZ         → teal-green  (radio mandatory only, dashed)
 *   OTHER       → steel-blue  (general controlled airspace, dashed)
 */
const ZONE_META: Record<string, ZoneMeta> = {
  PROHIBITED:  { label: 'Prohibited Airspace',      fillColor: 'rgba(176,  0,  0, 0.38)', lineColor: '#b00000' },
  RESTRICTED:  { label: 'Restricted Airspace',      fillColor: 'rgba(215, 55,  0, 0.24)', lineColor: '#d73700' },
  DANGER:      { label: 'Danger Zone',               fillColor: 'rgba(220,110,  0, 0.22)', lineColor: '#dc6e00' },
  CTR:         { label: 'Control Zone (CTR)',         fillColor: 'rgba(  0, 82,200, 0.20)', lineColor: '#0052c8' },
  TMA:         { label: 'Terminal Area (TMA)',        fillColor: 'rgba(190,  0,160, 0.16)', lineColor: '#be00a0', dash: true },
  TRA:         { label: 'Temporary Reserved Area',   fillColor: 'rgba(190,128,  0, 0.15)', lineColor: '#be8000', dash: true },
  ADIZ:        { label: 'Air Defense ID Zone',        fillColor: 'rgba(110,  0,200, 0.13)', lineColor: '#6e00c8', dash: true },
  RMZ:         { label: 'Radio Mandatory Zone',       fillColor: 'rgba(  0,140, 90, 0.13)', lineColor: '#008c5a', dash: true },
  OTHER:       { label: 'Controlled Airspace',        fillColor: 'rgba( 60,120,200, 0.08)', lineColor: '#3c78c8', dash: true },
};

const DEFAULT_META: ZoneMeta = ZONE_META.OTHER;

function metaFor(type: string): ZoneMeta {
  return ZONE_META[type] ?? DEFAULT_META;
}

/* ── Mapbox color expressions ──────────────────────────────────────────────── */

function matchExpr(getter: (m: ZoneMeta) => string, fallback: string): mapboxgl.Expression {
  const pairs: string[] = [];
  for (const [type, meta] of Object.entries(ZONE_META)) {
    pairs.push(type, getter(meta));
  }
  return ['match', ['get', 'type'], ...pairs, fallback] as unknown as mapboxgl.Expression;
}

const FILL_COLOR = matchExpr(m => m.fillColor, DEFAULT_META.fillColor);
const LINE_COLOR = matchExpr(m => m.lineColor, DEFAULT_META.lineColor);

/* ── Module state ──────────────────────────────────────────────────────────── */

let geojsonData: GeoJSON.FeatureCollection | null = null;
let panel: HTMLElement | null = null;
/** True only while the airspace layer group is enabled. Guards the map-wide click. */
let layerEnabled = false;
/** Popup shown when a click lands on ground with no mapped restrictions. */
let noRestrictionsPopup: mapboxgl.Popup | null = null;

/* ── Data loading ──────────────────────────────────────────────────────────── */

interface ChunkManifest {
  parts: string[];
  total_features: number;
}

/**
 * Load airspace from chunked parts. Fetches the manifest, then all parts in
 * parallel, and merges them into a single FeatureCollection. Returns null if the
 * manifest is missing or the fetch fails, so the caller can skip the layer
 * instead of throwing.
 */
async function loadAirspace(): Promise<GeoJSON.FeatureCollection | null> {
  if (geojsonData) return geojsonData;

  try {
    const manifest = await fetchJSON<ChunkManifest>('/data/airspace/manifest.json');
    const partCollections = await Promise.all(
      manifest.parts.map(p => fetchJSON<GeoJSON.FeatureCollection>(p)),
    );

    const features: GeoJSON.Feature[] = [];
    for (const fc of partCollections) {
      if (fc?.features) features.push(...fc.features);
    }

    geojsonData = { type: 'FeatureCollection', features };
    return geojsonData;
  } catch (err) {
    console.warn('[airspace] failed to load chunked data, skipping layer:', err);
    return null;
  }
}

/* ── Layer management ──────────────────────────────────────────────────────── */

export async function addAirspaceLayer(map: mapboxgl.Map): Promise<void> {
  const data = await loadAirspace();
  if (!data) return; // manifest missing / fetch failed — skip the layer
  if (map.getSource(SOURCE_ID)) return;

  map.addSource(SOURCE_ID, { type: 'geojson', data });

  const beforeLayer = map.getLayer('airport-large') ? 'airport-large' : undefined;

  // Fill
  map.addLayer({
    id:      LAYER_FILL,
    type:    'fill',
    source:  SOURCE_ID,
    minzoom: 4,
    paint: {
      'fill-color':   FILL_COLOR,
      'fill-opacity': 1,
    },
  }, beforeLayer);

  // Border — PROHIBITED slightly thicker; dashed for advisory zones
  map.addLayer({
    id:      LAYER_LINE,
    type:    'line',
    source:  SOURCE_ID,
    minzoom: 4,
    paint: {
      'line-color':   LINE_COLOR,
      'line-width': [
        'interpolate', ['linear'], ['zoom'],
        4,  ['match', ['get', 'type'], 'PROHIBITED', 1.4, 'RESTRICTED', 1.0, 0.7],
        8,  ['match', ['get', 'type'], 'PROHIBITED', 2.4, 'RESTRICTED', 1.8, 1.4],
        12, ['match', ['get', 'type'], 'PROHIBITED', 3.0, 'RESTRICTED', 2.2, 1.8],
      ] as any,
      // Dashed lines for advisory/temporary zones (TMA, TRA, ADIZ, RMZ, OTHER)
      'line-dasharray': [
        'match', ['get', 'type'],
        'TMA',  ['literal', [6, 3]],
        'TRA',  ['literal', [6, 3]],
        'ADIZ', ['literal', [8, 4]],
        'RMZ',  ['literal', [4, 2]],
        'OTHER',['literal', [3, 2]],
        ['literal', [1, 0]],   // solid for PROHIBITED, RESTRICTED, DANGER, CTR
      ] as any,
      'line-opacity': 0.90,
    },
  }, beforeLayer);

  createPanel();
  // Map-wide click: assess flyability anywhere, not just on airspace polygons.
  // Guarded by layerEnabled so it only runs while the layer group is on.
  layerEnabled = true;
  map.on('click', onMapClick);
  map.on('mouseenter', LAYER_FILL, () => { map.getCanvas().style.cursor = 'pointer'; });
  map.on('mouseleave', LAYER_FILL, () => { map.getCanvas().style.cursor = ''; });
}

export function removeAirspaceLayer(map: mapboxgl.Map): void {
  layerEnabled = false;
  destroyPanel();
  noRestrictionsPopup?.remove();
  noRestrictionsPopup = null;
  map.off('click', onMapClick);
  for (const id of ALL_LAYERS) {
    if (map.getLayer(id)) map.removeLayer(id);
  }
  if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
}

/* ── "WHERE YOU CLICKED" panel ─────────────────────────────────────────────── */

function createPanel(): void {
  if (panel) return;
  panel = document.createElement('div');
  panel.className = 'as-panel';
  panel.style.display = 'none';
  document.getElementById('map')?.appendChild(panel);
}

function destroyPanel(): void {
  panel?.remove();
  panel = null;
}

/* ── Flyability assessment ─────────────────────────────────────────────────── */

interface PanelItem {
  category: 'airspace' | 'runway' | 'hazard';
  type: string;
  name: string;
  lower?: string;
  upper?: string;
  country?: string;
  maxAlt?: number;
  color?: string;
}

interface Verdict {
  level: 'SAFE' | 'CAUTION' | 'NO_FLY';
  label: string;
  color: string;
  icon: string;
  reasons: string[];
}

function assessFlyability(items: PanelItem[]): Verdict {
  const reasons: string[] = [];
  let level: 'SAFE' | 'CAUTION' | 'NO_FLY' = 'SAFE';

  for (const item of items) {
    if (item.category === 'airspace') {
      if (item.type === 'PROHIBITED') {
        level = 'NO_FLY';
        reasons.push(`Inside PROHIBITED zone: ${item.name}`);
      } else if (item.type === 'RESTRICTED' || item.type === 'CTR') {
        if (level !== 'NO_FLY') level = 'CAUTION';
        reasons.push(`Inside ${item.type} zone: ${item.name}`);
      } else if (item.type === 'DANGER') {
        if (level !== 'NO_FLY') level = 'CAUTION';
        reasons.push(`Inside DANGER zone: ${item.name}`);
      } else if (item.type === 'NATIONAL_PARK') {
        if (level !== 'NO_FLY') level = 'CAUTION';
        reasons.push(`Inside protected area: ${item.name}`);
      } else if (item.type === 'TMA') {
        if (level !== 'NO_FLY') level = 'CAUTION';
        reasons.push(`Inside Terminal Area: ${item.name}`);
      } else {
        if (level !== 'NO_FLY') level = 'CAUTION';
        reasons.push(`Inside ${item.type}: ${item.name}`);
      }
    } else if (item.category === 'runway') {
      if (item.type === 'no_fly') {
        level = 'NO_FLY';
        reasons.push(`Runway no-fly zone: ${item.name}`);
      } else if (item.type === 'altitude_60') {
        if (level !== 'NO_FLY') level = 'CAUTION';
        reasons.push(`Max 60m AGL near ${item.name}`);
      } else if (item.type === 'altitude_150') {
        if (level !== 'NO_FLY') level = 'CAUTION';
        reasons.push(`Max 150m AGL near ${item.name}`);
      } else if (item.type === 'ctr_5nm') {
        if (level !== 'NO_FLY') level = 'CAUTION';
        reasons.push(`Inside 5 NM control zone: ${item.name}`);
      } else if (item.type === 'ctr_boundary') {
        if (level !== 'NO_FLY') level = 'CAUTION';
        reasons.push(`Inside airport boundary: ${item.name}`);
      } else if (item.type === 'heliport_zone') {
        if (level !== 'NO_FLY') level = 'CAUTION';
        reasons.push(`Inside heliport zone: ${item.name}`);
      } else if (item.type === 'runway_outline') {
        if (level !== 'NO_FLY') level = 'CAUTION';
        reasons.push(`On runway: ${item.name}`);
      }
    } else if (item.category === 'hazard') {
      if (level !== 'NO_FLY') level = 'CAUTION';
      reasons.push(`Near ${item.type === 'prison' ? 'prison' : 'military zone'}: ${item.name}`);
    }
  }

  const verdicts = {
    SAFE:    { label: 'LIKELY SAFE TO FLY', color: '#22c55e', icon: '✓' },
    CAUTION: { label: 'USE CAUTION',        color: '#f59e0b', icon: '⚠' },
    NO_FLY:  { label: 'DO NOT FLY HERE',    color: '#ef4444', icon: '✕' },
  };

  return { level, reasons, ...verdicts[level] };
}

/* ── Info links per airspace classification ────────────────────────────────── */

const CLASSIFICATION_INFO: Record<string, { desc: string; url: string }> = {
  PROHIBITED:    { desc: 'Flight is prohibited — no drone operations allowed without special authorization.',
                   url: 'https://www.icao.int/safety/airnavigation/AIG/Pages/Airspace-Classification.aspx' },
  RESTRICTED:    { desc: 'Restricted zone — drone ops may be allowed with advance permission from controlling authority.',
                   url: 'https://www.icao.int/safety/airnavigation/AIG/Pages/Airspace-Classification.aspx' },
  CTR:           { desc: 'Control Zone — surrounds airports with ATC. Authorization usually required.',
                   url: 'https://skybrary.aero/articles/control-zone-ctr' },
  TMA:           { desc: 'Terminal Manoeuvring Area — controlled airspace for arriving/departing aircraft.',
                   url: 'https://skybrary.aero/articles/terminal-control-area-tma' },
  DANGER:        { desc: 'Danger Zone — activities hazardous to flight may occur. Extra caution required.',
                   url: 'https://skybrary.aero/articles/danger-area' },
  TRA:           { desc: 'Temporary Reserved Airspace — reserved for specific operations during set periods.',
                   url: 'https://skybrary.aero/articles/temporary-reserved-area-tra' },
  ADIZ:          { desc: 'Air Defense Identification Zone — military identification requirements apply.',
                   url: 'https://skybrary.aero/articles/air-defense-identification-zone-adiz' },
  RMZ:           { desc: 'Radio Mandatory Zone — two-way radio communication required.',
                   url: 'https://skybrary.aero/articles/radio-mandatory-zone-rmz' },
  NATIONAL_PARK: { desc: 'Protected area — drone launch/landing generally requires permits.',
                   url: 'https://www.nps.gov/subjects/aviation/unmanned-aircraft.htm' },
};

/* ── Panel rendering ──────────────────────────────────────────────────────── */

function showPanel(items: PanelItem[]) {
  if (!panel) return;

  const verdict = assessFlyability(items);

  const rows = items.map(item => {
    let swatchColor: string;
    let label: string;
    let name: string;
    let extra = '';
    let infoLink = '';

    if (item.category === 'airspace') {
      if (item.type === 'NATIONAL_PARK') {
        swatchColor = '#f9a825';
        label = 'PROTECTED AREA';
      } else {
        const meta = metaFor(item.type);
        swatchColor = meta.lineColor;
        label = meta.label.toUpperCase();
      }
      name = item.name || 'Unknown';
      if (item.lower && item.upper) extra = `<div class="as-panel-alt">${item.lower} – ${item.upper}</div>`;

      // Classification info link
      const info = CLASSIFICATION_INFO[item.type];
      if (info) {
        infoLink = `<a href="${info.url}" target="_blank" rel="noopener" class="as-panel-info-link" title="${info.desc}">What is this? →</a>`;
      }
    } else if (item.category === 'runway') {
      const zoneColors: Record<string, string> = {
        ctr_5nm: '#3b82f6', ctr_boundary: '#60a5fa', heliport_zone: '#a855f7',
        no_fly: '#ef4444', altitude_60: '#fb923c', altitude_150: '#facc15',
        runway_outline: '#ef4444',
      };
      swatchColor = item.color || zoneColors[item.type] || '#999';
      label = 'DRONE ZONE';
      name = item.name;
      if (item.maxAlt !== undefined) extra = `<div class="as-panel-alt">Max altitude: ${item.maxAlt}m AGL</div>`;
    } else {
      swatchColor = item.type === 'military' ? '#ef4444' : '#a855f7';
      label = item.type === 'military' ? 'MILITARY' : 'PRISON';
      name = item.name || item.type;
    }

    const country = item.country ? `<div class="as-panel-country">${item.country}</div>` : '';
    return `
      <div class="as-panel-item">
        <div class="as-panel-swatch" style="background:${swatchColor}"></div>
        <div class="as-panel-text">
          <div class="as-panel-label">${label}</div>
          <div class="as-panel-name">${name}</div>
          ${extra}${country}${infoLink}
        </div>
      </div>
    `;
  }).join('');

  panel.innerHTML = `
    <div class="as-panel-header-compact">
      <span class="as-panel-header-title">CAN I FLY HERE?</span>
      <button class="as-panel-close" aria-label="Close">✕</button>
    </div>
    <div class="as-panel-verdict-compact" style="background:${verdict.color}">
      <span class="as-panel-verdict-icon">${verdict.icon}</span>
      <span class="as-panel-verdict-text">${verdict.label}</span>
    </div>
    ${verdict.reasons.length ? `<div class="as-panel-reasons">${verdict.reasons.map(r => `<div class="as-panel-reason">• ${r}</div>`).join('')}</div>` : ''}
    <div class="as-panel-body">${rows}</div>
  `;
  panel.style.display = 'flex';

  panel.querySelector('.as-panel-close')?.addEventListener('click', () => {
    if (panel) panel.style.display = 'none';
  });
}

/* ── Click handler ─────────────────────────────────────────────────────────── */

/** All layers we query for the "Can I Fly Here?" assessment. */
const RUNWAY_LAYERS = [
  'runway-no-fly', 'runway-alt-60', 'runway-alt-150',
  'runway-ctr-5nm', 'runway-ctr-boundary', 'runway-heliport-zone',
  'runway-outline',
];
const HAZARD_LAYERS = [
  'hazard-mil-fill', 'hazard-mil-points',
  'hazard-prison-fill', 'hazard-prison-points',
];
const PARKS_LAYERS = ['drone-parks-fill'];

function onMapClick(e: mapboxgl.MapMouseEvent) {
  // Guard: only assess while the airspace layer group is enabled.
  if (!layerEnabled) return;

  const map = e.target as mapboxgl.Map;
  const items: PanelItem[] = [];
  const seen = new Set<string>();

  // 1. Airspace zones
  const airspaceFeatures = map.queryRenderedFeatures(e.point, { layers: [LAYER_FILL] });
  for (const f of airspaceFeatures) {
    const p = f.properties as Record<string, string>;
    const key = `airspace|${p.type}|${p.name}`;
    if (seen.has(key)) continue;
    seen.add(key);
    items.push({
      category: 'airspace', type: p.type, name: p.name,
      lower: p.lower ?? undefined, upper: p.upper ?? undefined, country: p.country ?? undefined,
    });
  }

  // 2. Runway zones
  const rwLayers = RUNWAY_LAYERS.filter(l => !!map.getLayer(l));
  if (rwLayers.length) {
    const rwFeatures = map.queryRenderedFeatures(e.point, { layers: rwLayers });
    for (const f of rwFeatures) {
      const p = f.properties as Record<string, string>;
      const key = `runway|${p.zone}|${p.icao}|${p.runway || ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      items.push({
        category: 'runway', type: p.zone,
        name: p.airport_name || p.icao,
        country: p.country ?? undefined,
        maxAlt: p.maxAlt !== undefined && p.maxAlt !== 'undefined' ? Number(p.maxAlt) : undefined,
      });
    }
  }

  // 3. Hazard layers
  const hzLayers = HAZARD_LAYERS.filter(l => !!map.getLayer(l));
  if (hzLayers.length) {
    const hzFeatures = map.queryRenderedFeatures(e.point, { layers: hzLayers });
    for (const f of hzFeatures) {
      const p = f.properties as Record<string, string>;
      const key = `hazard|${p.category}|${p.name}`;
      if (seen.has(key)) continue;
      seen.add(key);
      items.push({ category: 'hazard', type: p.category, name: p.name || p.category });
    }
  }

  // 4. Protected areas (parks) — now in Drone Awareness layer
  const pkLayers = PARKS_LAYERS.filter(l => !!map.getLayer(l));
  if (pkLayers.length) {
    const pkFeatures = map.queryRenderedFeatures(e.point, { layers: pkLayers });
    for (const f of pkFeatures) {
      const p = f.properties as Record<string, string>;
      const key = `park|${p.name}`;
      if (seen.has(key)) continue;
      seen.add(key);
      items.push({ category: 'airspace', type: 'NATIONAL_PARK', name: p.name || 'Protected Area', country: p.country ?? undefined });
    }
  }

  // Nothing mapped at this point: show a SAFE-styled "no restrictions" popup.
  // We do not stop propagation, so country selection still works underneath.
  if (items.length === 0) {
    showNoRestrictions(map, e.lngLat);
    return;
  }

  // Sort: no-fly/prohibited first, safe zones last
  const ORDER: Record<string, number> = {
    no_fly: 0, PROHIBITED: 1, RESTRICTED: 2, CTR: 3, altitude_60: 4,
    TMA: 5, DANGER: 6, ctr_5nm: 7, ctr_boundary: 8, altitude_150: 9,
    military: 10, prison: 11, heliport_zone: 12, runway_outline: 12.5,
    ADIZ: 13, TRA: 14, RMZ: 15, OTHER: 16, NATIONAL_PARK: 17,
  };
  items.sort((a, b) => (ORDER[a.type] ?? 15) - (ORDER[b.type] ?? 15));

  showPanel(items);
}

/**
 * SAFE-styled popup shown when a click lands where no restrictions are mapped.
 * Airspace data is incomplete for many countries, so this is not a guarantee.
 */
function showNoRestrictions(map: mapboxgl.Map, lngLat: mapboxgl.LngLatLike): void {
  // Close any open "where you clicked" panel so the two do not stack.
  if (panel) panel.style.display = 'none';

  noRestrictionsPopup?.remove();
  noRestrictionsPopup = new mapboxgl.Popup({ closeButton: true, maxWidth: '260px' })
    .setLngLat(lngLat)
    .setHTML(`
      <div class="as-noflag" style="min-width:200px">
        <div class="as-noflag-verdict" style="display:flex;align-items:center;gap:8px;background:#22c55e;color:#062012;font-weight:700;font-size:12px;letter-spacing:0.4px;padding:8px 10px;border-radius:8px;margin-bottom:8px">
          <span class="as-noflag-icon" style="font-size:14px">✓</span>
          <span class="as-noflag-title">NO MAPPED RESTRICTIONS</span>
        </div>
        <div class="as-noflag-body" style="font-size:12px;line-height:1.5;color:var(--color-text-secondary, #b4b8c8);padding-right:14px">
          No mapped restrictions at this point. Airspace data is not complete for every country. Verify with the local aviation authority before flying.
        </div>
      </div>
    `)
    .addTo(map);
}
